import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
  Calendar,
  CheckCircle,
  CheckSquare,
  ChartBar,
  ChartLine,
  ChatCircle,
  Clock,
  CreditCard,
  Crown,
  Envelope,
  FileText,
  GearSix,
  House,
  Key,
  Link as LinkIcon,
  Megaphone,
  PencilSimple,
  Question,
  ShieldCheck,
  Sparkle,
  Storefront,
  User,
} from '@phosphor-icons/react';

export type StaffRole = 'admin' | 'account_manager' | 'lead_specialist' | 'specialist';

export type StaffRoute =
  '/subdomain/admin' | '/subdomain/admin/businesses' | '/subdomain/admin/staff' | '/subdomain/admin/workbench/reviews';

export interface StaffNavItem {
  id: string;
  label: string;
  icon: PhosphorIcon;
  /** Omitted = screen not built yet. Shown (per the design) but not a link. */
  to?: StaffRoute;
  /** Stay highlighted on the pages under this one (a business's projects, a person's profile). */
  matchChildren?: boolean;
  soon?: boolean;
}

export interface StaffNavGroup {
  /** Heading shown when the rail is expanded; null for the first group. */
  heading: string | null;
  items: StaffNavItem[];
}

const item = (id: string, label: string, icon: PhosphorIcon, extra: Partial<StaffNavItem> = {}): StaffNavItem => ({
  id,
  label,
  icon,
  ...extra,
});

// Figma A01.3 (admin, expanded rail 432:30750). The rest arrive with their sprints.
const ALL = {
  dashboard: item('dashboard', 'Dashboard', House, { to: '/subdomain/admin' }),
  businesses: item('businesses', 'Businesses', Storefront, { to: '/subdomain/admin/businesses', matchChildren: true }),
  meetings: item('meetings', 'Meetings', Calendar),
  pendingReports: item('pending-reports', 'Pending reports', FileText),
  leads: item('leads', 'Leads', Megaphone, { soon: true }),
  taskIntake: item('task-intake', 'Task intake', CheckSquare),
  reviewQueue: item('review-queue', 'Review queue', CheckCircle, {
    to: '/subdomain/admin/workbench/reviews',
    matchChildren: true,
  }),
  clientChat: item('client-chat', 'Client chat', ChatCircle),
  teamChat: item('team-chat', 'Team chat', Envelope),
  staff: item('staff', 'Staff', User, { to: '/subdomain/admin/staff', matchChildren: true }),
  dispatch: item('dispatch', 'Capacity & dispatch', ChartBar),
  ledger: item('ledger', 'Ledger', ChartLine),
  subscriptions: item('subscriptions', 'Subscriptions', Crown),
  invoices: item('invoices', 'Invoices & GST', FileText),
  addonOrders: item('addon-orders', 'Add-on orders', Sparkle),
  plans: item('plans', 'Plans & add-ons', CreditCard),
  blog: item('blog', 'Blog posts', PencilSimple),
  help: item('help', 'Help articles', Question),
  audit: item('audit', 'Audit log', ShieldCheck),
  webhooks: item('webhooks', 'Webhooks', LinkIcon),
  flags: item('flags', 'Feature flags', GearSix),
  // Lead / specialist
  workbench: item('workbench', 'My workbench', House, { to: '/subdomain/admin' }),
  schedule: item('schedule', 'My schedule', Clock),
  assets: item('assets', 'Assets & brand kits', Key),
};

/** Role-scoped rail from the four A01.3 variants. Hide what a role cannot use (KT §7.1). */
export function navForRole(role: StaffRole): StaffNavGroup[] {
  switch (role) {
    case 'admin':
      return [
        { heading: 'Overview', items: [ALL.dashboard] },
        { heading: 'Clients', items: [ALL.businesses, ALL.meetings, ALL.pendingReports, ALL.leads] },
        { heading: 'Work', items: [ALL.taskIntake, ALL.reviewQueue, ALL.clientChat, ALL.teamChat] },
        { heading: 'Team', items: [ALL.staff, ALL.dispatch] },
        { heading: 'Money', items: [ALL.ledger, ALL.subscriptions, ALL.invoices, ALL.addonOrders, ALL.plans] },
        { heading: 'Content', items: [ALL.blog, ALL.help] },
        { heading: 'System', items: [ALL.audit, ALL.webhooks, ALL.flags] },
      ];
    case 'account_manager':
      return [
        { heading: 'Overview', items: [ALL.dashboard] },
        { heading: 'Clients', items: [ALL.businesses, ALL.meetings, ALL.pendingReports, ALL.leads] },
        { heading: 'Work', items: [ALL.taskIntake, ALL.reviewQueue, ALL.clientChat, ALL.teamChat] },
        { heading: 'Money', items: [ALL.subscriptions, ALL.addonOrders] },
      ];
    case 'lead_specialist':
      return [
        {
          heading: 'Work',
          items: [ALL.workbench, ALL.taskIntake, ALL.reviewQueue, ALL.dispatch, ALL.schedule, ALL.assets],
        },
        { heading: 'Chat', items: [ALL.clientChat, ALL.teamChat] },
      ];
    case 'specialist':
      return [
        { heading: 'Work', items: [ALL.workbench, ALL.schedule, ALL.assets] },
        { heading: 'Chat', items: [ALL.clientChat, ALL.teamChat] },
      ];
  }
}

/** Figma top-bar scope chip + search placeholder per role. Counts need an API (TODO(api)), so only the label shows. */
export const ROLE_TOPBAR: Record<StaffRole, { scope?: string; searchPlaceholder: string }> = {
  admin: { scope: 'All clients', searchPlaceholder: 'Search clients, tasks, invoices…' },
  account_manager: { scope: 'My clients', searchPlaceholder: 'Search my clients, tasks…' },
  lead_specialist: { searchPlaceholder: 'Search tasks, clients, assets…' },
  specialist: { searchPlaceholder: 'Search my tasks and assets…' },
};

export const ROLE_LABEL: Record<StaffRole, string> = {
  admin: 'Admin',
  account_manager: 'Account manager',
  lead_specialist: 'Lead specialist',
  specialist: 'Specialist',
};

export const isStaffRoleName = (role: string | null | undefined): role is StaffRole =>
  role === 'admin' || role === 'account_manager' || role === 'lead_specialist' || role === 'specialist';
